/**
 * Pre-upload image pipelines. Two of them, chosen by the PURPOSE of the upload:
 *
 *   compressImageForUpload — evidence photos. Downscaled JPEG. The default.
 *   compressBrandImage     — logos and signatures. Downscaled PNG, alpha intact.
 *   compressAvatarImage    — profile pictures. Small JPEG (a photo, no alpha).
 *
 * The rest of this note describes the evidence path, which is unchanged.
 *
 * Every capture path (phone camera, phone library, desktop file picker) is re-encoded
 * to a downscaled JPEG before it reaches POST /api/uploads. Two reasons:
 *
 *   HEIC — iPhone library photos are HEIC. iOS decodes HEIC natively, so the canvas
 *   draw below succeeds and the *output* is JPEG. Merely accepting HEIC server-side
 *   would not be enough: Chrome, Firefox and Edge cannot render it, so the photo
 *   would upload fine and then show as broken in the app and be missing from the
 *   certificate.
 *
 *   Size — current phone photos routinely exceed the server's 5 MB cap, and evidence
 *   photos are embedded into PDFs that get emailed, so raw camera files produce
 *   certificates that are slow to render and too large to send.
 *
 * Orientation: phone photos carry EXIF Orientation. Browsers already apply it when
 * rendering an <img>, and naturalWidth/naturalHeight report the oriented dimensions,
 * so drawing that element to a canvas bakes the correct rotation into the output
 * pixels. Same EXIF-orientation bake-in the server-rendered certificates rely on.
 */

/** Long edge cap. 2000px keeps a switchboard label readable while landing well under 1 MB. */
const MAX_EDGE_PX = 2000;
const JPEG_QUALITY = 0.8;
const JPEG_MIME = 'image/jpeg';

/**
 * Brand path (logos, and signatures once those are built): PNG so the alpha
 * channel survives. A logo matted onto white prints as black marks inside a
 * solid white rectangle stamped over the report layout.
 *
 * PNG is far larger than JPEG at the same dimensions, so the long edge is capped
 * much lower — a header logo or a signature block is never rendered near 2000px,
 * and the logo endpoint enforces a 2 MB limit.
 */
const BRAND_MAX_EDGE_PX = 800;
const PNG_MIME = 'image/png';

/**
 * Avatar path: a profile picture is a PHOTOGRAPH of a person, so it wants JPEG —
 * not the brand path's PNG. A photographic PNG at 800px lands roughly 600 KB–1.5 MB,
 * which is under the endpoint's 2 MB cap but not comfortably: that trades a reliable
 * 413 for an intermittent one, which is worse. JPEG at quality 0.8 and the same
 * small cap comes out around 100–150 KB.
 *
 * Separate constant from BRAND_MAX_EDGE_PX despite the matching value: the two caps
 * answer different questions and should be tunable independently.
 */
const AVATAR_MAX_EDGE_PX = 800;

const HEIC_HINT = /hei[cf]/i;
const UNREADABLE_MESSAGE =
  "This image couldn't be read. Try another photo, or convert it to JPEG first.";
const HEIC_MESSAGE =
  "This photo is in Apple's HEIC format, which this browser can't open. "
  + 'Take the photo on your phone, or convert it to JPEG first.';
const UNSUPPORTED_MESSAGE =
  "This browser can't process photos for upload. Try a different browser or device.";

function decodeMessageFor(file) {
  const isHeic = HEIC_HINT.test(file?.type || '') || HEIC_HINT.test(file?.name || '');
  return isHeic ? HEIC_MESSAGE : UNREADABLE_MESSAGE;
}

/**
 * Decode via an <img> element rather than createImageBitmap: element decoding applies
 * EXIF orientation on every browser we support, and iOS Safari's support for
 * createImageBitmap options is too recent to rely on.
 */
function decodeImage(file) {
  return new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(file);
    const release = () => URL.revokeObjectURL(objectUrl);
    const img = new Image();
    img.onload = () => resolve({ img, release });
    img.onerror = () => {
      release();
      reject(new Error(decodeMessageFor(file)));
    };
    img.src = objectUrl;
  });
}

/** Scale the long edge down to maxEdgePx, preserving aspect ratio. Never upscales. */
function fitWithinMaxEdge(width, height, maxEdgePx) {
  const longEdge = Math.max(width, height);
  if (longEdge <= maxEdgePx) return { width, height };
  const scale = maxEdgePx / longEdge;
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
}

/** quality is ignored by the PNG encoder; passed through for the JPEG path only. */
function canvasToBlob(canvas, mime, quality) {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error(UNREADABLE_MESSAGE))),
      mime,
      quality
    );
  });
}

function withExtension(name, extension, fallbackBase) {
  const base = String(name || '').replace(/\.[^./\\]*$/, '').trim();
  return `${base || fallbackBase}.${extension}`;
}

/**
 * Shared decode → downscale → re-encode. The canvas draw is what bakes EXIF
 * orientation into the output pixels, so both paths get it.
 *
 * @param {File} file
 * @param {{ maxEdgePx: number, mime: string, quality?: number, extension: string,
 *           fallbackBase: string, matteWhite: boolean }} spec
 */
async function reencode(file, spec) {
  if (!file) throw new Error('No photo selected.');

  const { img, release } = await decodeImage(file);
  try {
    const sourceWidth = img.naturalWidth || img.width;
    const sourceHeight = img.naturalHeight || img.height;
    if (!sourceWidth || !sourceHeight) throw new Error(decodeMessageFor(file));

    const { width, height } = fitWithinMaxEdge(sourceWidth, sourceHeight, spec.maxEdgePx);
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;

    const ctx = canvas.getContext('2d');
    if (!ctx || typeof canvas.toBlob !== 'function') throw new Error(UNSUPPORTED_MESSAGE);

    if (spec.matteWhite) {
      // JPEG has no alpha channel; matte transparency onto white instead of black.
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, width, height);
    }
    ctx.drawImage(img, 0, 0, width, height);

    const blob = await canvasToBlob(canvas, spec.mime, spec.quality);
    return new File([blob], withExtension(file.name, spec.extension, spec.fallbackBase), {
      type: spec.mime,
      lastModified: Date.now(),
    });
  } finally {
    release();
  }
}

/**
 * Re-encode a user-selected image as a downscaled JPEG ready for upload.
 *
 * Throws an Error whose message is safe to show the user when the browser cannot decode
 * the file (typically HEIC on desktop). Callers must surface that message and must not
 * fall back to uploading the original file — that only moves the rejection to the server
 * and makes a client format limitation look like a server bug.
 *
 * @param {File} file Image chosen from a file input.
 * @returns {Promise<File>} JPEG file, long edge at most 2000px.
 */
export async function compressImageForUpload(file) {
  return reencode(file, {
    maxEdgePx: MAX_EDGE_PX,
    mime: JPEG_MIME,
    quality: JPEG_QUALITY,
    extension: 'jpg',
    fallbackBase: 'photo',
    matteWhite: true,
  });
}

/**
 * Re-encode a logo or signature as a downscaled PNG, preserving transparency.
 *
 * Deliberately a SEPARATE named export rather than an option on
 * compressImageForUpload: the evidence pipeline must be impossible to reach this
 * behaviour by accident. An options argument with a default is exactly how an
 * evidence photo would one day be silently exported without its white matte (or
 * at the wrong size cap), and nobody would notice until a certificate looked
 * wrong.
 *
 * Selection is by PURPOSE, decided by the caller. This never sniffs the MIME type
 * or looks for transparent pixels — a JPEG logo is fine to send through here.
 *
 * Throws the same user-safe messages as compressImageForUpload.
 *
 * @param {File} file Image chosen from a file input.
 * @returns {Promise<File>} PNG file with alpha intact, long edge at most 800px.
 */
export async function compressBrandImage(file) {
  return reencode(file, {
    maxEdgePx: BRAND_MAX_EDGE_PX,
    mime: PNG_MIME,
    extension: 'png',
    fallbackBase: 'logo',
    matteWhite: false,
  });
}

/**
 * Re-encode a profile picture as a small JPEG ready for upload.
 *
 * The evidence spec with the small size cap: JPEG suits a photograph, and an
 * avatar has no transparency to preserve. Without this, a phone photo used as a
 * profile picture is posted raw and rejected by the 2 MB avatar endpoint — and,
 * if it is HEIC, refused as an unsupported format. Both go away here.
 *
 * A third named export rather than a flag, for the same reason the brand path is
 * separate: purpose decides the pipeline.
 *
 * Throws the same user-safe messages as compressImageForUpload.
 *
 * @param {File} file Image chosen from a file input.
 * @returns {Promise<File>} JPEG file, long edge at most 800px.
 */
export async function compressAvatarImage(file) {
  return reencode(file, {
    maxEdgePx: AVATAR_MAX_EDGE_PX,
    mime: JPEG_MIME,
    quality: JPEG_QUALITY,
    extension: 'jpg',
    fallbackBase: 'avatar',
    matteWhite: true,
  });
}
